import {
    DynamicComponentExitReason,
    DynamicComponentOptions,
} from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { querySlot } from '@treasury/utils/functions';

export const DRAWER_CONTENT_CONTAINER_ID = 'tm-side-drawer-content';

/**
 * Get drawer content from the slot containing it in `<tm-side-drawer>`.
 */
export function getContentFromSlot(slot: HTMLSlotElement) {
    const container = querySlot(slot, `#${DRAWER_CONTENT_CONTAINER_ID}`);
    return container?.firstElementChild;
}

export enum SideDrawerButtonPosture {
    Primary = 'primary',
    Secondary = 'secondary',
    Tertiary = 'tertiary',
    Constructive = 'approve',
    Destructive = 'reject',
}

export interface SideDrawerButton<T = unknown> {
    label: string;
    reason: DynamicComponentExitReason;
    posture?: SideDrawerButtonPosture;
    /**
     * Provide a custom predicate to determine if the button is enabled.
     *
     * @param data Any data that has been set from within the drawer using `setData()`
     * @returns `true`, if the button is enabled.
     */
    checkEnabled?: (data?: T) => boolean;
    onClick?: (close: (data?: T) => void, setData: (data: T) => void) => void;
}

export class SideDrawerProvider<T = unknown> {
    private _drawerButtons?: SideDrawerButton<T>[];

    public get drawerButtons() {
        return this._drawerButtons;
    }

    /**
     * Specify an array of button configuration objects
     * to be used by `TmSideDrawerService`
     */
    public provideButtons(buttons: SideDrawerButton<T>[]) {
        this._drawerButtons = buttons;
        return this;
    }
}

/**
 * Components that wish to specify configuration in a way that is
 * encapsulated within the class itself should implement this interface.
 */
export interface SideDrawerProviderHost<T = unknown> extends Element {
    readonly provider: SideDrawerProvider<T>;
}

export function isProviderHost<T = unknown>(obj: Element): obj is SideDrawerProviderHost<T> {
    return 'provider' in obj && obj.provider instanceof SideDrawerProvider;
}

export interface SideDrawerOptions extends DynamicComponentOptions {
    align: 'left' | 'right';
    footerAlignment: 'left' | 'right';
    buttons: SideDrawerButton[];
}

const DRAWER_RESULT_EVENT = `tm-drawer-result`;

export function listenForDrawerResult<T>(element: Element, callback: (data: T) => void) {
    const listener = (e: Event) => {
        if (!(e instanceof CustomEvent)) {
            throw new Error(`Invalid event instance for "${DRAWER_RESULT_EVENT}"`);
        }

        callback((e as CustomEvent<T>).detail);
    };

    element.addEventListener(DRAWER_RESULT_EVENT, listener);

    return {
        unsubscribe: () => element.removeEventListener(DRAWER_RESULT_EVENT, listener),
    };
}

/**
 * Dispatch side drawer data on an element in a strongly-typed way.
 * Intended to be used from a component rendered within `TmSideDrawer`.
 */
export function dispatchDrawerResult<T>(element: Element, data: T) {
    const event = new CustomEvent(DRAWER_RESULT_EVENT, {
        detail: data,
        bubbles: true,
        composed: true,
    });

    element.dispatchEvent(event);
}
